import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { Col, Form, Input, Modal, ModalProps, Row } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { DefaultOptionType } from 'antd/es/select';
import { memo, useCallback, useEffect, useState } from 'react';

import { SalesInfoApis } from 'apis/SalesInfoApis';
import { Button, InputForm, SelectForm } from 'components';
import {
  ICheckEcodeSerialParams,
  IGetAllConditionTypeParams,
  IPromotion,
} from 'interfaces/SalesInfo';
import { toastError, toastSuccess } from 'utils/constants/functions';

const { TextArea } = Input;

type VoucherModalProps = Omit<ModalProps, 'onOk' | 'onCancel'> & {
  promotionData: IPromotion;
  onCancel?: () => void;
  onOk?(form: any): void;
  onBeforeOk?: (form: any) => { isExist: boolean };
};

export const VoucherModal = memo(
  ({ promotionData, open, onOk, onCancel, onBeforeOk }: VoucherModalProps) => {
    const [form] = useForm();

    const [conditionTypes, setConditionTypes] = useState(
        [] as DefaultOptionType[],
      ),
      [voucher, setVoucher] = useState<any>(undefined),
      [statusDetail, setStatusDetail] = useState('');

    const {
        mutate: checkEcodeSerialMutate,
        isPending: isCheckEcodeSerialPending,
      } = useMutation({
        mutationFn: (params: ICheckEcodeSerialParams) =>
          SalesInfoApis.checkEcodeSerial(params),
        onSuccess: (res: any) => {
          const results = res?.Output?.results;
          const statuses = res?.Status?.results;
          const statusDetail = statuses[0]?.Status;
          const [_, ...others] = Object.keys(form.getFieldsValue());

          setStatusDetail(statusDetail);

          if (results?.length > 0) {
            getAllConditionTypesMutate({
              checkEcodeSerialResult: results ?? [],
              getConditionTypeParams: {
                conditionTypeId: results[0]?.ConditionType,
              },
            });

            return;
          }

          form.resetFields(others);
          toastError(statusDetail);
        },
        onError: (error) => {
          console.log(error);
          toastError('Mã voucher không hợp lệ!');
        },
      }),
      { mutate: getAllConditionTypesMutate } = useMutation({
        mutationFn: ({
          getConditionTypeParams,
        }: {
          checkEcodeSerialResult: any;
          getConditionTypeParams: IGetAllConditionTypeParams;
        }) => SalesInfoApis.getAllConditionTypes(getConditionTypeParams),
        onSuccess: (res: any, variables) => {
          if (res?.data?.getConditionType) {
            const { checkEcodeSerialResult } = variables;
            const results = checkEcodeSerialResult;
            const [result, ..._] = checkEcodeSerialResult;
            const { __metadata, ...voucher } = result;

            if (result?.Status === 'Active') {
              setVoucher(voucher);

              form.setFieldsValue({
                conditionType: result?.Voucher,
                saleDeal: result?.SaleDeal,
                saleDealName: result?.SaleDealName,
                discountValue: result?.DiscountValue,
                dateRange: `${result?.SerialFromDate} - ${result?.SerialToDate}`,
                status: result?.Status,
                saleDealType: res?.data?.getConditionType?.items[0]?.value,
              });

              setConditionTypes(
                results.map(
                  (item: any) =>
                    ({
                      label: item?.VoucherName,
                      value: item?.Voucher,
                    }) as DefaultOptionType,
                ),
              );

              toastSuccess(statusDetail);
              return;
            }

            const [__, ...others] = Object.keys(form.getFieldsValue());
            form.resetFields(others);

            setVoucher(undefined);
            toastError(statusDetail);
            return;
          }

          toastError(
            `Không thể lấy ConditionType của voucher. ${res?.errors[0]?.message}!`,
          );
        },
        onError: (error) => {
          toastError(
            `Không thể lấy ConditionType của voucher. ${error?.message ?? error}!`,
          );
        },
      });

    useEffect(() => {
      if (!open) form.resetFields();
    }, [open]);

    const handleCheckSaleDeal = useCallback(() => {
      const { Key, SaleOrg, Channel, ..._ } = promotionData;
      const serialValue = form.getFieldValue('serial');

      if (!serialValue?.trim()) {
        setStatusDetail('');
        toastError('Vui lòng nhập phiếu ưu đãi!');
        return;
      }

      checkEcodeSerialMutate({
        Key,
        SaleOrg,
        Channel,
        Serial: serialValue,
      });
    }, [promotionData]);

    const handleOnCancel = useCallback(() => {
      setConditionTypes([]);
      setVoucher(undefined);
      onCancel && onCancel();
    }, [onCancel]);

    const handleOnFinish = useCallback(
      (values: any) => {
        if (!voucher) return;

        if (onBeforeOk && onBeforeOk({ SaleDeal: voucher?.SaleDeal }).isExist)
          return;

        onOk &&
          onOk({
            ...voucher,
            ConditionValue: voucher?.DiscountValue,
            Note: values?.note,
            Type: 'vnd',
          });
        handleOnCancel();
      },
      [voucher, promotionData, onOk, onBeforeOk],
    );

    return (
      <Modal
        title="Áp dụng Phiếu ưu đãi"
        open={open}
        className="!w-[1000px]"
        onCancel={handleOnCancel}
        footer={false}
      >
        <Form layout="vertical" form={form} onFinish={handleOnFinish}>
          <Form.Item label="Serial" name="serial">
            <div className="flex items-center space-x-2">
              <div className="flex-grow">
                <InputForm
                  name="serial"
                  placeholder="Serial"
                  className="h-full"
                  onChange={(e) => form.setFieldValue('serial', e.target.value)}
                />
              </div>
              <div className="flex-none h-full">
                <Button
                  fill
                  type="button"
                  title="Kiểm tra"
                  customClass="!mt-0 min-w-[120px]"
                  iconBefore={
                    isCheckEcodeSerialPending ? <LoadingOutlined /> : <></>
                  }
                  disabled={isCheckEcodeSerialPending}
                  handleOnclick={handleCheckSaleDeal}
                />
              </div>
            </div>
          </Form.Item>
          <div className="grid grid-cols-3 gap-4">
            <Form.Item label="Condition type" name="conditionType">
              <SelectForm
                placeholder="Condition type"
                size="large"
                options={conditionTypes}
              />
            </Form.Item>
            <Form.Item label="Mã chương trình" name="saleDeal">
              <InputForm size="large" placeholder="Mã chương trình" readOnly />
            </Form.Item>
            <Form.Item label="Chương trình" name="saleDealName">
              <InputForm size="large" placeholder="Chương trình" readOnly />
            </Form.Item>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <Form.Item label="Loại KM" name="saleDealType">
              <InputForm pattern="Loại KM" placeholder="Loại KM" readOnly />
            </Form.Item>
            <Form.Item label="Giá trị/phần trăm CK" name="discountValue">
              <InputForm
                size="large"
                placeholder="Giá trị/phần trăm CK"
                readOnly
              />
            </Form.Item>
            <Form.Item label="Thời hạn" name="dateRange">
              <InputForm placeholder="Thời hạn" readOnly />
            </Form.Item>
          </div>
          <Form.Item label="Ghi chú" name="note">
            <TextArea
              placeholder="Nhập ghi chú (giới hạn 250 kí tự)"
              maxLength={250}
            />
          </Form.Item>
          {statusDetail && (
            <Form.Item name="status">
              {form.getFieldValue('status') === 'Active' ? (
                <Row>
                  <Col>
                    <CheckCircleOutlined className="text-green-500 mr-2" />
                  </Col>
                  <Col>{statusDetail}</Col>
                </Row>
              ) : (
                <Row>
                  <Col>
                    <ExclamationCircleOutlined className="text-red-500 mr-2" />
                  </Col>
                  <Col>{statusDetail}</Col>
                </Row>
              )}
            </Form.Item>
          )}
          <div className="flex justify-end mt-4 gap-2">
            <Button
              title="Quay lại"
              type="button"
              customClass="!border-transparent !text-neutral-30 !bg-transparent"
              disabled={isCheckEcodeSerialPending}
              handleOnclick={handleOnCancel}
            />
            <Button
              fill
              type="submit"
              title="Thêm CTKM"
              disabled={isCheckEcodeSerialPending || !voucher}
            />
          </div>
        </Form>
      </Modal>
    );
  },
);
